import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { motion } from 'framer-motion'
import { Mail, Lock, User, UserCheck, ArrowLeft, Home } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast/headless'
import { useAuthStore } from '../hooks/useAuthStore'

const LoginPage = ({ onBackToHome }) => {
  const { t } = useTranslation('auth')
  const [isLogin, setIsLogin] = useState(true)
  const [isGuest, setIsGuest] = useState(false)
  const [isForgotPassword, setIsForgotPassword] = useState(false)
  const { register: registerUser, login, loginAsGuest, resetPassword, isLoading } = useAuthStore()

  const { register, handleSubmit, formState: { errors }, reset } = useForm()

  const onSubmit = async (data) => {
    if (isForgotPassword) {
      const success = await resetPassword(data.email)
      if (success) {
        toast.success(t('resetEmailSent'))
        reset()
        switchMode('login')
      }
      return
    }

    let success = false

    if (isGuest) {
      success = await loginAsGuest(data.username)
    } else if (isLogin) {
      success = await login({ email: data.email, password: data.password })
    } else {
      success = await registerUser({
        email: data.email,
        password: data.password,
        username: data.username
      })
    }

    if (success) {
      reset()
    }
  }

  const switchMode = (mode) => {
    setIsLogin(mode === 'login')
    setIsGuest(mode === 'guest')
    setIsForgotPassword(mode === 'forgot')
    reset()
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900">
      {/* Header with back to home button */}
      {onBackToHome && (
        <motion.header
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4"
        >
          <button
            onClick={onBackToHome}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors duration-200 group"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <Home className="w-5 h-5" />
            <span>{t('backToHome')}</span>
          </button>
        </motion.header>
      )}

      <div className="flex items-center justify-center p-4" style={{ minHeight: onBackToHome ? 'calc(100vh - 80px)' : '100vh' }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
        <div className="glass-effect rounded-xl p-8 shadow-2xl">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">{t('gameLobby')}</h1>
            <p className="text-gray-400">{t('joinMultiplayer')}</p>
          </div>

          {isForgotPassword ? (
            <h2 className="text-center text-lg font-medium text-white mb-6">{t('forgotPassword')}</h2>
          ) : (
            <div className="flex gap-2 mb-6 p-1 bg-gray-800 rounded-lg">
              <button
                onClick={() => switchMode('login')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  isLogin && !isGuest
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {t('login')}
              </button>
              <button
                onClick={() => switchMode('register')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  !isLogin && !isGuest
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {t('register')}
              </button>
              <button
                onClick={() => switchMode('guest')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  isGuest
                    ? 'bg-green-600 text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {t('guest')}
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {isForgotPassword ? (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('email')}
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    {...register('email', {
                      required: t('validation.emailRequired'),
                      pattern: { value: /^\S+@\S+$/i, message: t('validation.emailInvalid') }
                    })}
                    type="email"
                    className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t('enterEmail')}
                  />
                </div>
                {errors.email && (
                  <p className="text-red-400 text-sm mt-1">{errors.email.message}</p>
                )}
              </div>
            ) : isGuest ? (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('username')}
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    {...register('username', {
                      required: t('validation.usernameRequired'),
                      minLength: { value: 3, message: t('validation.usernameMinLength') }
                    })}
                    type="text"
                    className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={t('enterUsername')}
                  />
                </div>
                {errors.username && (
                  <p className="text-red-400 text-sm mt-1">{errors.username.message}</p>
                )}
              </div>
            ) : (
              <>
                {!isLogin && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t('username')}
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        {...register('username', {
                          required: !isLogin ? t('validation.usernameRequired') : false,
                          minLength: { value: 3, message: t('validation.usernameMinLength') }
                        })}
                        type="text"
                        className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder={t('enterUsername')}
                      />
                    </div>
                    {errors.username && (
                      <p className="text-red-400 text-sm mt-1">{errors.username.message}</p>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t('email')}
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      {...register('email', {
                        required: t('validation.emailRequired'),
                        pattern: { value: /^\S+@\S+$/i, message: t('validation.emailInvalid') }
                      })}
                      type="email"
                      className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={t('enterEmail')}
                    />
                  </div>
                  {errors.email && (
                    <p className="text-red-400 text-sm mt-1">{errors.email.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t('password')}
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      {...register('password', {
                        required: t('validation.passwordRequired'),
                        minLength: { value: 6, message: t('validation.passwordMinLength') }
                      })}
                      type="password"
                      className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={t('enterPassword')}
                    />
                  </div>
                  {errors.password && (
                    <p className="text-red-400 text-sm mt-1">{errors.password.message}</p>
                  )}
                </div>

                {isLogin && (
                  <div className="text-center -mt-2">
                    <button
                      type="button"
                      onClick={() => switchMode('forgot')}
                      className="text-sm text-blue-400 hover:text-blue-300"
                    >
                      {t('forgotPassword')}
                    </button>
                  </div>
                )}
              </>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white font-medium rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <UserCheck className="w-5 h-5" />
                  {isForgotPassword ? t('sendResetEmail') : isGuest ? t('continueAsGuest') : isLogin ? t('login') : t('register')}
                </>
              )}
            </button>
          </form>

          {isForgotPassword ? (
            <div className="mt-6 text-center text-sm">
              <button
                onClick={() => switchMode('login')}
                className="text-blue-400 hover:text-blue-300 font-medium"
              >
                {t('backToLogin')}
              </button>
            </div>
          ) : !isGuest && (
            <div className="mt-6 text-center text-sm text-gray-400">
              {isLogin ? t('dontHaveAccount') + ' ' : t('alreadyHaveAccount') + ' '}
              <button
                onClick={() => switchMode(isLogin ? 'register' : 'login')}
                className="text-blue-400 hover:text-blue-300 font-medium"
              >
                {isLogin ? t('registerHere') : t('loginHere')}
              </button>
            </div>
          )}
        </div>
        </motion.div>
      </div>
    </div>
  )
}

export default LoginPage